function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import cn from 'classnames';
import { useGlobalContext } from 'seagull';
import { extractPureTextFromRichText, TextButton, Typography, Badge, Tooltip, NLFField, MenuItem } from 'seagull';
import { EditLine } from 'seagull/icons';
import map from 'lodash/map';
import { ServiceCostType, ServiceStatusType } from "../../models/service/model";
import { getServiceBillingText, getServicePaymentText } from "../../utils/service-description";
import { ServicePriceEditor } from "../ServicePriceEditor/ServicePriceEditor";
import SimpleTextModal from "./SimpleTextModal";
import { getServiceDescriptionBadges } from "./ServiceDetails.utils";
import PriceDisplay from "./PriceDisplay";
import styles from "./ServiceDetails.module.scss";
import { billingDayOfMonthModel, serviceModel } from "../../models";
import { utils } from "../../utils";
import { ordinal } from "../../infra/utils";
import { validateBillingDayOfMonth } from "../../models/service";
import { useStoreActions } from "../../contexts";
import ConditionalWrapper from "../ConditionalWrapper/ConditionalWrapper";
import { daysOfWeek, defaultBillingDayOfWeek } from "../../constants";
import { RelationshipStatus } from "../../models/relationship";
export default function ServiceDetails(props) {
  const {
    serviceToDisplay,
    testid,
    serviceType,
    menuButton,
    agreementStatus,
    agreementData,
    useBadges,
    additionalSections,
    isVendor,
    disableEditBillUpfront,
    disableEditBillingDay,
    disableStatusBadge,
    onCostChange,
    onHasError,
    onDayOfMonthChange,
    onDayOfWeekChange,
    onBillUpfrontModeChange
  } = props;
  const isActiveAgreement = agreementStatus === RelationshipStatus.Approved;
  const {
    name,
    description,
    cost
  } = serviceToDisplay;
  const [isHovered, setIsHovered] = useState(false);
  const [priceEditorOpen, setPriceEditorOpen] = useState(false);
  const globalContext = useGlobalContext();
  const descriptionRef = useRef(null);
  const editorButtonRef = useRef(null);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const showDiscount = cost.discount && cost.discount.amount.greaterThan(0);
  const isOriginalService = serviceType === 'original';
  const pureTextDescription = useMemo(() => extractPureTextFromRichText(description), [description]);
  const isEditing = onDayOfWeekChange && onDayOfMonthChange && onBillUpfrontModeChange;
  useEffect(() => {
    const newIsOverflowing = !!descriptionRef.current && descriptionRef.current.clientWidth < descriptionRef.current.scrollWidth;
    setIsDescriptionOverflowing(newIsOverflowing);
  }, [descriptionRef]);
  const onClickDescription = async () => {
    await globalContext.showModal({
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(SimpleTextModal, {
          newDesign: true,
          title: name,
          text: description,
          onClose: () => dismissModal(true)
        });
      },
      closeOnClickOutside: true,
      closeOnEscapeKey: true
    });
  };
  const getDiscountString = () => {
    const {
      cost
    } = serviceToDisplay;
    const discount = cost.discount;
    return (discount === null || discount === void 0 ? void 0 : discount.type) === 'percent' ? "".concat(discount.amount, "% Off") : "$".concat(discount === null || discount === void 0 ? void 0 : discount.amount, " Off");
  };
  const priceDisplayProps = useMemo(() => {
    const {
      cost
    } = serviceToDisplay;
    const discount = cost.discount;
    const getRangePriceString = (minPrice, maxPrice) => minPrice === null || minPrice.isZero() ? "Up to ".concat(utils.formatDollarAmount(maxPrice)) : "".concat(utils.formatDollarAmount(minPrice), " - ").concat(utils.formatDollarAmount(maxPrice));
    switch (cost.pricingType) {
      case ServiceCostType.Fixed:
        return {
          priceAfterDiscount: utils.formatDollarAmount(utils.applyDiscount(cost.price, discount)),
          priceBeforeDiscount: discount ? utils.formatDollarAmount(cost.price) : undefined
        };
      case ServiceCostType.Variable:
        return {
          priceAfterDiscount: utils.formatDollarAmount(utils.applyDiscount(cost.price, discount)),
          priceBeforeDiscount: discount ? utils.formatDollarAmount(cost.price) : undefined,
          postfixPrice: "/".concat(cost.unitName || 'hr')
        };
      case ServiceCostType.Range:
        {
          const finalMaxPrice = utils.applyDiscount(cost.maxPrice, discount);
          const priceAfterDiscount = getRangePriceString(cost.minPrice ? utils.applyDiscount(cost.minPrice, discount) : null, finalMaxPrice);
          let priceBeforeDiscount;
          if (discount) {
            priceBeforeDiscount = getRangePriceString(cost.minPrice, cost.maxPrice);
          }
          return {
            priceAfterDiscount,
            priceBeforeDiscount
          };
        }
    }
  }, [serviceToDisplay]);
  const statusBadgeText = useMemo(() => {
    if (disableStatusBadge) {
      return null;
    }
    switch (serviceType) {
      case ServiceStatusType.Amended:
        return 'Amended';
      case ServiceStatusType.AwaitingApproval:
        return isActiveAgreement ? 'New Service' : null;
      case 'original':
        return 'Original';
      case ServiceStatusType.Completed:
        return 'Completed';
      case ServiceStatusType.Terminated:
        return 'Stopped';
      case ServiceStatusType.Canceled:
        return 'Canceled';
      default:
        return null;
    }
  }, [serviceType, isActiveAgreement, disableStatusBadge]);
  const bulletPoints = useMemo(() => {
    const {
      cost
    } = serviceToDisplay;
    const isVariablePrice = serviceModel.isVariablePrice(cost);
    const isRangePrice = serviceModel.isRangePrice(cost);
    const billingText = getServiceBillingText(serviceToDisplay, {
      isActiveAgreement,
      effectiveDate: agreementData === null || agreementData === void 0 ? void 0 : agreementData.effectiveDate
    });
    return billingText.concat(getServicePaymentText({
      unitCap: isVariablePrice ? cost.unitCap : undefined,
      unitName: isVariablePrice ? cost.unitName : undefined,
      maxPrice: isRangePrice ? cost.maxPrice : undefined
    }, agreementData));
  }, [serviceToDisplay, isActiveAgreement, agreementData]);
  const badgesList = useMemo(() => {
    return getServiceDescriptionBadges(serviceToDisplay, isActiveAgreement);
  }, [serviceToDisplay, isActiveAgreement]);
  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.serviceDetails, {
      [styles.original]: isOriginalService
    }),
    "data-testid": "".concat(testid, "service-details"),
    "data-billupfront": JSON.stringify('isBilledUpfront' in serviceToDisplay.billing && serviceToDisplay.billing.isBilledUpfront),
    "data-serviceid": serviceToDisplay.id
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.descriptionAndMenuButtonContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.detailedDescription, {
      [styles.withBadge]: !!statusBadgeText
    })
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    ellipsis: true,
    "data-testid": testid + 'name'
  }, name), /*#__PURE__*/React.createElement("div", {
    className: cn(styles.descriptionWrapper),
    "data-testid": "".concat(testid, "service-details-description")
  }, /*#__PURE__*/React.createElement(Typography, {
    ellipsis: true,
    hideTooltip: true,
    className: cn(styles.description),
    ref: descriptionRef,
    skin: "secondary",
    "data-testid": testid + 'description'
  }, pureTextDescription), isDescriptionOverflowing && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/React.createElement(TextButton, {
    onClick: onClickDescription
  }, "Read more")))), /*#__PURE__*/React.createElement("div", {
    className: styles.badgeAndDotMenuContainer
  }, serviceModel.isPaused(serviceToDisplay) && serviceModel.isActive(serviceToDisplay) && /*#__PURE__*/React.createElement(Tooltip, {
    title: isVendor ? 'Automatic billing is paused for this service. To resume, go to the Billing Hub.' : 'Automatic billing is paused for this service.',
    placement: "top"
  }, /*#__PURE__*/React.createElement(Badge, {
    "data-testid": "service-details-paused-badge"
  }, "Paused")), statusBadgeText && /*#__PURE__*/React.createElement(Badge, {
    outlined: true,
    "data-testid": "".concat(testid, "status-badge")
  }, statusBadgeText), !isOriginalService && menuButton)), /*#__PURE__*/React.createElement("div", {
    className: styles.finalPriceContainer
  }, onCostChange ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextButton, {
    onClick: () => setPriceEditorOpen(true),
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    underline: "none",
    suffixIcon: (isHovered || priceEditorOpen) && /*#__PURE__*/React.createElement(EditLine, null),
    ref: editorButtonRef
  }, /*#__PURE__*/React.createElement(PriceDisplay, _extends({}, priceDisplayProps, {
    isInButton: isHovered || priceEditorOpen,
    "data-testid": testid + 'inline-edit-'
  }))), /*#__PURE__*/React.createElement(ServicePriceEditor, {
    cost: serviceToDisplay.cost,
    editorAnchor: editorButtonRef.current,
    isOpen: priceEditorOpen,
    setIsOpen: setPriceEditorOpen,
    onCostChange: onCostChange
  })) : /*#__PURE__*/React.createElement(PriceDisplay, _extends({}, priceDisplayProps, {
    "data-testid": testid
  })), !useBadges && showDiscount && /*#__PURE__*/React.createElement("div", {
    "data-testid": testid + 'discount'
  }, /*#__PURE__*/React.createElement(Badge, {
    skin: "success"
  }, getDiscountString()))), useBadges ? /*#__PURE__*/React.createElement("div", {
    className: styles.badgesList
  }, badgesList.map(_ref2 => {
    let {
      text,
      intent,
      skin,
      tooltip
    } = _ref2;
    return /*#__PURE__*/React.createElement(Tooltip, {
      key: intent,
      title: tooltip || '',
      placement: "top"
    }, /*#__PURE__*/React.createElement(Badge, {
      rounded: true,
      outlined: true,
      skin: skin,
      "data-testid": testid + "badge-".concat(intent),
      "data-intent": intent
    }, text));
  })) : /*#__PURE__*/React.createElement("ul", {
    className: styles.bulletsList
  }, map(bulletPoints, (_ref3, idx) => {
    let {
      text,
      intent
    } = _ref3;
    return /*#__PURE__*/React.createElement("li", {
      key: idx,
      className: cn(styles.listItem)
    }, /*#__PURE__*/React.createElement("span", {
      className: styles.bullet
    }), /*#__PURE__*/React.createElement(Typography, {
      "data-testid": testid + "bullet-".concat(idx),
      "data-intent": intent
    }, text));
  })), isEditing && /*#__PURE__*/React.createElement(BillingSettingsSection, {
    testid: testid,
    service: serviceToDisplay,
    isActiveAgreement: isActiveAgreement,
    disableBillUpfront: disableEditBillUpfront,
    disableBillingDay: disableEditBillingDay,
    onHasError: onHasError,
    onDayOfMonthChange: onDayOfMonthChange,
    onDayOfWeekChange: onDayOfWeekChange,
    onBillUpfrontModeChange: onBillUpfrontModeChange
  }), additionalSections && additionalSections.map((_ref4, idx) => {
    let {
      value,
      disableBorder
    } = _ref4;
    return value && /*#__PURE__*/React.createElement("div", {
      className: cn(styles.additionalSection, disableBorder && styles.disableBorder),
      key: idx
    }, value);
  }));
}
function BillingSettingsSection(props) {
  const {
    service,
    testid,
    isActiveAgreement,
    disableBillingDay,
    disableBillUpfront,
    onHasError,
    onBillUpfrontModeChange,
    onDayOfMonthChange,
    onDayOfWeekChange
  } = props;
  const [showBillingDayOfMonthError, setShowBillingDayOfMonthError] = useState(false);
  const {
    logBI
  } = useStoreActions();
  const isBilledUpfront = useMemo(() => serviceModel.isBilledUpfront(service) || false, [service]);
  const serviceId = serviceModel.id(service);
  const billingTrigger = serviceModel.billingTrigger(service);
  const dayOfMonthOptions = useMemo(() => {
    return new Array(27).fill(null).map((_, idx) => ({
      value: (idx + 1).toString(),
      display: ordinal(idx + 1),
      disabled: false
    })).concat({
      value: 'last',
      display: 'last day',
      disabled: isBilledUpfront
    });
  }, [isBilledUpfront]);
  const onBillingDayOfMonthChange = useCallback(value => {
    const isLast = value === dayOfMonthOptions[dayOfMonthOptions.length - 1].value;
    const newBillingDayOfMonth = billingDayOfMonthModel.makeBillingDayOfMonth(isLast ? 'last' : +value);
    const hasError = validateBillingDayOfMonth(newBillingDayOfMonth, isBilledUpfront);
    setShowBillingDayOfMonthError(hasError);
    onHasError && onHasError(serviceId, hasError);
    onDayOfMonthChange && onDayOfMonthChange(serviceId, newBillingDayOfMonth);
  }, [isBilledUpfront, dayOfMonthOptions, onDayOfMonthChange, onHasError, serviceId]);
  const onBillUpfrontChange = useCallback(value => {
    const billingDayOfMonth = serviceModel.billingDayOfMonth(service);
    const hasError = billingDayOfMonth != null && validateBillingDayOfMonth(billingDayOfMonth, value);
    setShowBillingDayOfMonthError(hasError);
    onHasError && onHasError(serviceId, hasError);
    onBillUpfrontModeChange && onBillUpfrontModeChange(serviceId, value);
  }, [onBillUpfrontModeChange, onHasError, serviceId, service]);
  useEffect(() => {
    setShowBillingDayOfMonthError(serviceModel.isMonthlyService(service) && validateBillingDayOfMonth(serviceModel.billingDayOfMonth(service), serviceModel.isBilledUpfront(service)));
  }, [service]);
  if (serviceModel.isOngoing(billingTrigger)) {
    const {
      billingDayOfMonth
    } = billingTrigger;
    return /*#__PURE__*/React.createElement("div", {
      className: styles.billingSettingsContainer
    }, /*#__PURE__*/React.createElement(ConditionalWrapper, {
      condition: showBillingDayOfMonthError,
      wrapper: children => /*#__PURE__*/React.createElement(Tooltip, {
        placement: "right",
        title: /*#__PURE__*/React.createElement(Typography, {
          skin: "invertedStandard"
        }, "It is not possible to bill upfront on the last day of the month. Please change the billing day.")
      }, children)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.billingSettingsSection
    }, /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      className: styles.text
    }, "I'm billing"), /*#__PURE__*/React.createElement(NLFField, {
      skin: "primary",
      disabled: disableBillUpfront,
      value: JSON.stringify(isBilledUpfront),
      onChange: e => {
        const isBilledUpfront = JSON.parse(e.target.value);
        logBI(11016, {
          isActiveAgreement,
          serviceId: serviceModel.id(service),
          isBilledUpfront
        });
        onBillUpfrontChange(isBilledUpfront);
      },
      testid: "".concat(testid, "is-billed-upfront-dropdown")
    }, /*#__PURE__*/React.createElement(MenuItem, {
      key: 'start',
      value: JSON.stringify(true),
      "data-testid": "".concat(testid, "is-billed-upfront-option-1")
    }, "upfront", ' ', serviceModel.isQuarterlyService(service) ? 'on the 1st day of every quarter' : ''), /*#__PURE__*/React.createElement(MenuItem, {
      key: 'end',
      value: JSON.stringify(false),
      "data-testid": "".concat(testid, "is-billed-upfront-option-0")
    }, serviceModel.isQuarterlyService(service) ? 'on the last day of every quarter' : 'at the end of the billing cycle')), serviceModel.isMonthlyService(service) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      className: styles.text
    }, "on the"), /*#__PURE__*/React.createElement(NLFField, {
      skin: "primary",
      disabled: disableBillingDay,
      value: billingDayOfMonth && !billingDayOfMonthModel.isLastDayOfMonth(billingDayOfMonth) ? billingDayOfMonth.day.toString() : 'last',
      onChange: e => {
        logBI(11014, {
          isActiveAgreement,
          serviceId: serviceModel.id(service),
          day: e.target.value
        });
        onBillingDayOfMonthChange(e.target.value);
      },
      testid: "".concat(testid, "billing-day-dropdown"),
      error: showBillingDayOfMonthError
    }, dayOfMonthOptions.map((option, idx) => {
      const value = option.value || idx.toString();
      const display = option.display || option;
      return /*#__PURE__*/React.createElement(MenuItem, {
        key: value,
        value: value,
        disabled: option.disabled,
        "data-testid": "".concat(testid, "billing-day-option-").concat(idx)
      }, display);
    })), /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      className: styles.text
    }, "of each month")) : serviceModel.isWeeklyService(service) || serviceModel.isBiWeeklyService(service) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      className: styles.text
    }, "on"), /*#__PURE__*/React.createElement(NLFField, {
      skin: "primary",
      disabled: disableBillingDay,
      value: serviceModel.billingDayOfWeek(service),
      onChange: e => {
        logBI(11015, {
          isActiveAgreement,
          serviceId: serviceModel.id(service),
          day: e.target.value
        });
        onDayOfWeekChange && onDayOfWeekChange(serviceModel.id(service), e.target.value);
      },
      testid: "".concat(testid, "billing-day-dropdown")
    }, daysOfWeek.map((option, idx) => {
      const value = option || idx.toString();
      const display = option || option;
      return /*#__PURE__*/React.createElement(MenuItem, {
        key: value,
        value: value,
        "data-testid": "".concat(testid, "billing-day-option-").concat(idx)
      }, display);
    }))) : null)));
  } else if (serviceModel.isRepeatableManual(billingTrigger) && !serviceModel.isYearlyService(service)) {
    const {
      billingDayOfMonth
    } = billingTrigger;
    return /*#__PURE__*/React.createElement("div", {
      className: styles.billingSettingsContainer
    }, /*#__PURE__*/React.createElement(ConditionalWrapper, {
      condition: showBillingDayOfMonthError,
      wrapper: children => /*#__PURE__*/React.createElement(Tooltip, {
        placement: "right",
        title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
          skin: "invertedStandard"
        }, "It is not possible to bill upfront on the last day of the month. Please change the billing day."))
      }, children)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.billingSettingsSection
    }, !serviceModel.isQuarterlyService(service) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "".concat(testid, "billing-period-label"),
      noWrap: true,
      className: styles.text
    }, "Billing period ", isBilledUpfront ? 'starts' : 'ends', " on", ' ', serviceModel.isMonthlyService(service) ? 'the' : ''), serviceModel.isMonthlyService(service) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(NLFField, {
      skin: "primary",
      disabled: disableBillingDay,
      value: billingDayOfMonth && !billingDayOfMonthModel.isLastDayOfMonth(billingDayOfMonth) ? billingDayOfMonth.day.toString() : 'last',
      onChange: e => {
        logBI(11014, {
          isActiveAgreement,
          serviceId: serviceModel.id(service),
          day: e.target.value
        });
        onBillingDayOfMonthChange(e.target.value);
      },
      testid: "".concat(testid, "billing-day-dropdown"),
      error: showBillingDayOfMonthError
    }, dayOfMonthOptions.map((option, idx) => {
      const value = option.value || idx.toString();
      const display = option.display || option;
      return /*#__PURE__*/React.createElement(MenuItem, {
        key: value,
        value: value,
        disabled: option.disabled,
        "data-testid": "".concat(testid, "billing-day-option-").concat(idx)
      }, display);
    })), /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      className: styles.text
    }, "of each month")) : serviceModel.isWeeklyService(service) || serviceModel.isBiWeeklyService(service) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(NLFField, {
      skin: "primary",
      disabled: disableBillingDay,
      value: serviceModel.billingDayOfWeek(service) || defaultBillingDayOfWeek,
      onChange: e => {
        logBI(11015, {
          isActiveAgreement,
          serviceId: serviceModel.id(service),
          day: e.target.value
        });
        onDayOfWeekChange && onDayOfWeekChange(serviceModel.id(service), e.target.value);
      },
      testid: "".concat(testid, "billing-day-dropdown")
    }, daysOfWeek.map((option, idx) => {
      const value = option || idx.toString();
      const display = option || option;
      return /*#__PURE__*/React.createElement(MenuItem, {
        key: value,
        value: value,
        "data-testid": "".concat(testid, "billing-day-option-").concat(idx)
      }, display);
    })), /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      className: styles.text
    }, "of", ' ', serviceModel.isWeeklyService(service) ? 'each week' : 'every other week')) : null) : null)));
  } else {
    return null;
  }
}